import { ObjectDocumentSource } from "../../constants";

/*
  A task hangs off a parent object, and the parent decides what the overlay can offer: where the Visibility
  audience comes from, which documents can be attached, and whether the owner organisation is the acting
  org or the user's choice. Those answers used to be derived ad hoc at each use site (an `isDealWorkflow`
  boolean here, a `parentObjectType === "landholding"` check there), so adding a parent kind meant finding
  every one of them. They live in one table instead: a new parent is a new row, and `Record<TaskParentKind,
  ...>` fails the build until every capability is answered for it.
*/
export type TaskParentKind = "project" | "deal" | "landholding";

export interface TaskParentPolicy {
  /*
    Where the Visibility picker's organisations come from. "projectOrganisations" reads them off the parent
    project; "currentOrgOnly" means there is no project to ask, so the task is visible to the creating org
    alone and the field is hidden rather than shown empty on a required control.
  */
  visibility: "projectOrganisations" | "currentOrgOnly";
  /*
    Whether the owner organisation is fixed to the acting org or chosen by the user. Only a deal fixes it:
    a landholding task keeps its stored owner, so an edit by another org must not silently reassign it.
  */
  ownerOrganisation: "currentOrg" | "userChoice";
  // The document store this parent's attachments come from — the source the link accepts (BR-02811), and
  // the scope the upload modal uploads against.
  documents: {
    source: ObjectDocumentSource;
    scope: "project" | "deal" | "group";
  };
}

export const TASK_PARENT_POLICY: Record<TaskParentKind, TaskParentPolicy> = {
  project: {
    visibility: "projectOrganisations",
    ownerOrganisation: "userChoice",
    documents: { source: ObjectDocumentSource.PROJECT_DOCUMENT, scope: "project" },
  },
  deal: {
    visibility: "currentOrgOnly",
    ownerOrganisation: "currentOrg",
    documents: { source: ObjectDocumentSource.DEAL_DOCUMENT, scope: "deal" },
  },
  landholding: {
    // A landholding's documents are project documents attached to the group rather than to a project.
    visibility: "currentOrgOnly",
    ownerOrganisation: "userChoice",
    documents: { source: ObjectDocumentSource.PROJECT_DOCUMENT, scope: "group" },
  },
};

/*
  The parent object types the overlay can hang off, as stored on the task (objectType / workflowObjectType).
  Anything else — an activity relation, or no parent at all — has no policy and no parent-driven behaviour.
*/
export const resolveTaskParentKind = (objectType: string | null | undefined): TaskParentKind | undefined => {
  switch (objectType?.toLowerCase()) {
    case "project":
      return "project";
    case "deal":
      return "deal";
    case "landholding":
      return "landholding";
    default:
      return undefined;
  }
};
